import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { fetchDocumo, patientbyDoctor } from "../components/redux/doctorSlice";

const CLINICIAN_ROLES = [
  "Nurse",
  "Doctor",
  "PharmacistClinician",
  "NutritionalPractitioner",
  "Nutritionist",
  "FertilitySupport",
  "FertilityEducator",
  "Pharmacist",
];

const POLL_INTERVAL_MS = 60000;

function seenStorageKey(userId) {
  return `clinicianSeenFaxes_${userId}`;
}

function loadSeen(userId) {
  const raw = localStorage.getItem(seenStorageKey(userId));
  if (raw == null) return null;
  try {
    return new Set(JSON.parse(raw));
  } catch {
    return null;
  }
}

function saveSeen(userId, seenSet) {
  try {
    localStorage.setItem(seenStorageKey(userId), JSON.stringify(Array.from(seenSet)));
  } catch {
    // storage unavailable -> alerts will just re-fire next session
  }
}


export default function useClinicianFaxAlerts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userAuth = useSelector((state) => state?.authentication?.userAuth);
  const documoData = useSelector((state) => state.doctor?.documoData);
  const documoDateRange = useSelector((state) => state.doctor?.documoDateRange);

  const role = userAuth?.obj?.role;
  const userId = userAuth?.obj?.id;
  const isClinician = !!role && CLINICIAN_ROLES.includes(role);

  const myPatientUlisRef = useRef(new Set());
  const seenRef = useRef(null);
  const patientsLoadedRef = useRef(false);
  const dateRangeRef = useRef(null);

  useEffect(() => {
    dateRangeRef.current = documoDateRange;
  }, [documoDateRange]);

  const loadMyPatients = useCallback(async () => {
    const response = await dispatch(patientbyDoctor({ page: 0, size: 1000 }));
    const list = response?.payload?.data;
    if (Array.isArray(list)) {
      myPatientUlisRef.current = new Set(
        list.map((p) => String(p.uli || "").trim().toLowerCase()).filter(Boolean),
      );
    }
    patientsLoadedRef.current = true;
  }, [dispatch]);

  useEffect(() => {
    if (!isClinician || !userId) return;
    seenRef.current = loadSeen(userId);
    patientsLoadedRef.current = false;
    loadMyPatients();
  }, [isClinician, userId, loadMyPatients]);

  useEffect(() => {
    if (!isClinician || !userId) return undefined;

    
    const poll = () => dispatch(fetchDocumo(dateRangeRef.current || undefined));
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isClinician, userId, dispatch]);

  useEffect(() => {
    if (!isClinician || !userId || !patientsLoadedRef.current) return;
    if (!Array.isArray(documoData) || documoData.length === 0) return;

    const mine = documoData.filter(
      (f) =>
        f.mappedToPatient &&
        f.patientRef &&
        myPatientUlisRef.current.has(String(f.patientRef).trim().toLowerCase()),
    );
    if (mine.length === 0) return;

    if (seenRef.current === null) {
      
      seenRef.current = new Set(mine.map((f) => f.messageNumber));
      saveSeen(userId, seenRef.current);
      return;
    }

    const fresh = mine.filter((f) => !seenRef.current.has(f.messageNumber));
    if (fresh.length === 0) return;

    fresh.forEach((f) => {
      notification.info({
        message: "New fax received",
        description: `${f.patientName || "A patient"} received a new fax (ULI: ${f.patientRef}).`,
        placement: "topRight",
        duration: 8,
        onClick: () => navigate("/doctor/fax"),
      });
      seenRef.current.add(f.messageNumber);
    });

    saveSeen(userId, seenRef.current);
  }, [documoData, isClinician, userId, navigate]);
}
